import * as React from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "lib/utils";
import { Input } from "components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import {
  CountryOption,
  getLocalizedCountryName,
  getSortedCountryOptions,
} from "util/countries";

export interface CountryComboboxProps {
  id?: string;
  label: string;
  /** Selected country as its canonical English name (matches the redux slice). */
  value: string;
  onChange: (englishName: string) => void;
  required?: boolean;
  /** Show error styling + message (typically `touched && !valid`). */
  error?: boolean;
  errorMessage?: string;
  onBlur?: () => void;
}

/**
 * Searchable country selector (issue #868). ShadCN/Tailwind combobox built on
 * the existing `Popover` + `Input` primitives (no `cmdk`/new dependency),
 * reusing the shared `countries` util. Replaces the legacy MUI
 * `CountryAutocomplete` while preserving its type-to-search UX.
 */
const CountryCombobox: React.FC<CountryComboboxProps> = ({
  id = "country-combobox",
  label,
  value,
  onChange,
  required = false,
  error = false,
  errorMessage,
  onBlur,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language ?? "en";
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const options = React.useMemo(
    () => getSortedCountryOptions(language),
    [language]
  );
  const selected = React.useMemo<CountryOption | undefined>(
    () => options.find((o) => o.english === value),
    [options, value]
  );
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      getLocalizedCountryName(o, language).toLowerCase().includes(q)
    );
  }, [options, query, language]);

  const handleSelect = (option: CountryOption) => {
    onChange(option.english);
    setOpen(false);
    setQuery("");
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
      onBlur?.();
    }
  };

  const errorId = `${id}-error`;
  const listboxId = `${id}-listbox`;

  return (
    <div className="w-full space-y-1">
      <label htmlFor={id} className="text-sm font-medium text-slate-900">
        {label}
        {required && (
          <span aria-hidden="true" className="text-appRed ml-0.5">
            *
          </span>
        )}
      </label>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-label={label}
            aria-required={required || undefined}
            aria-invalid={error || undefined}
            aria-describedby={error ? errorId : undefined}
            className={cn(
              "flex h-14 w-full items-center justify-between rounded-xl border bg-white px-4 text-base transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ring-offset-background",
              error ? "border-appRed" : "border-black",
              selected ? "text-slate-900" : "text-slate-500"
            )}
          >
            <span className="truncate">
              {selected
                ? getLocalizedCountryName(selected, language)
                : t("book_location.personal_data.country")}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <div className="p-2">
            <Input
              autoFocus
              inputSize="sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("book_location.personal_data.country")}
              aria-label={t("book_location.personal_data.country")}
            />
          </div>
          <ul
            id={listboxId}
            className="max-h-60 overflow-y-auto pb-2"
            role="listbox"
          >
            {filtered.length === 0 ? (
              <li className="px-4 py-2 text-sm text-slate-500">
                {t("common.no_results")}
              </li>
            ) : (
              filtered.map((option) => {
                const isSelected = option.english === value;
                return (
                  <li key={option.english} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      onClick={() => handleSelect(option)}
                      className={cn(
                        "flex w-full items-center justify-between px-4 py-2 text-left text-sm text-slate-900 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline-none"
                      )}
                    >
                      <span className="truncate">
                        {getLocalizedCountryName(option, language)}
                      </span>
                      {isSelected && <Check className="ml-2 h-4 w-4 shrink-0" />}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </PopoverContent>
      </Popover>
      {error && errorMessage && (
        <p id={errorId} className="text-sm text-appRed mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default CountryCombobox;
